/**
 * 한 줄 작성기 — 모임 기록을 남기는 입구.
 *
 * 안내문에 장소 이름이 들어갑니다. 그래야 "여기에 쓰면 첨성대 글이 된다" 는 걸
 * 읽는 순간 알 수 있고, **장소를 고르는 단계가 설명 없이 사라집니다.**
 * 앱은 이미 어디인지 알고 있습니다(여행은 useNowSpot, 일반 모임은 다음 장소).
 *
 * 누르는 자리가 둘입니다.
 *   가운데(글)      남기기 시트 — 글부터
 *   오른쪽 사진      사진 선택기 → 사진이 채워진 시트
 * 사진을 따로 둔 이유는 사진이 주 재료이기 때문입니다. 대부분 이미 찍어 둔
 * 사진을 올리러 들어옵니다.
 *
 * 메인탭(홈 오늘 카드)과 모임 상세가 **같은 부품**을 씁니다. 두 곳에서 모양이
 * 갈라지면 학습이 두 번 필요합니다.
 */
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pickPhotoFile, shouldTellUser } from 'common/utils/photoPick';
import { isNativeApp } from 'common/webview';
import { useAlert } from 'contexts/AlertContext';
import { IcCamera } from 'components/common/icons/EmojiIcons';
import styles from './JournalComposer.module.css';

interface Props {
    /** 기록이 붙을 장소 이름 — 안내문에 그대로 들어갑니다 */
    placeName?: string | null;
    /** 내 프로필 이미지 */
    profileUrl?: string | null;
    /** 내 닉네임 — 프로필 이미지가 없을 때 첫 글자를 씁니다 */
    nickname?: string | null;
    /** 글부터 쓰기 */
    onWrite: () => void;
    /** 사진을 고른 뒤 시트 열기 */
    onPickPhotos: (files: File[]) => void;
    disabled?: boolean;
}

export const JournalComposer: React.FC<Props> = ({
    placeName, profileUrl, nickname, onWrite, onPickPhotos, disabled,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const fileRef = useRef<HTMLInputElement>(null);

    const hint = placeName
        ? t('journal.composerAt', { place: placeName })
        : t('journal.composerPlain');

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        // 같은 사진을 다시 골라도 change 가 나도록 비웁니다
        e.target.value = '';
        if (files.length > 0) onPickPhotos(files);
    };

    return (
        <div className={styles.composer}>
            <button
                type="button"
                className={styles.main}
                onClick={onWrite}
                disabled={disabled}
            >
                <span className={styles.avatar} aria-hidden="true">
                    {profileUrl
                        ? <img src={profileUrl} alt="" />
                        : <span className={styles.avatarText}>{(nickname || '').slice(0, 1)}</span>}
                </span>
                <span className={styles.hint}>{hint}</span>
            </button>

            <button
                type="button"
                className={styles.camera}
                onClick={() => {
                    /* 네이티브인지 **동기로 먼저** 가릅니다. await 뒤에는 사용자
                       제스처가 끝나 웹뷰가 input.click() 을 무시합니다 — 갤러리를
                       닫고 돌아왔는데 아무 일도 없던 이유입니다. */
                    if (!isNativeApp()) { fileRef.current?.click(); return; }
                    void (async () => {
                        const res = await pickPhotoFile();
                        if (res.ok) { onPickPhotos([res.file]); return; }
                        /* 취소는 실패가 아닙니다 — 아무 말도 하지 않습니다 */
                        if (shouldTellUser(res.reason)) {
                            showAlert({ message: t('journal.photoPickFailed') });
                        }
                    })();
                }}
                disabled={disabled}
                aria-label={t('journal.addPhoto')}
            >
                <IcCamera size={17} />
            </button>

            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFiles}
            />
        </div>
    );
};
